import { Vec3 } from '../../engine/math/vec3';
import type { Ctx } from '../Ctx';
import type { Player } from './Player';

export const SWING_TIME = 0.42;

/**
 * Sword attack (§3.1 LMB). One swing = anticipation → sweep → recover; a press during recovery
 * queues the next swing of a 3-hit combo. The hitbox is a sphere in front of the player during the sweep.
 */
export class Combat {
  t = -1;
  combo = 0;
  private queued = false;
  private hitThisSwing = new Set<object>();
  readonly hitCenter = new Vec3();
  hitRadius = 1.25;
  /** Called with the active hitbox every step it is live. */
  onHitbox?: (center: Vec3, r: number, hitSet: Set<object>) => void;

  constructor(private readonly ctx: Ctx, private readonly player: Player) {}

  get attacking(): boolean { return this.t >= 0; }
  get phase(): number { return this.t < 0 ? 0 : this.t / SWING_TIME; }
  get hitboxActive(): boolean { const k = this.phase; return this.attacking && k > 0.28 && k < 0.6; }

  private start(): void {
    this.t = 0;
    this.hitThisSwing.clear();
    this.queued = false;
    this.player.attackT = 0;
    this.player.attackCombo = this.combo;
    this.ctx.sfx.swing();
    this.player.noiseLevel = 1;
    // small forward lunge
    const f = _v.set(Math.sin(this.player.facing), 0, Math.cos(this.player.facing));
    this.player.velocity.addScaled(f, 1.2);
  }

  update(dt: number, hasSword: boolean): void {
    const pl = this.player, input = this.ctx.input;
    const pressed = input.wasButtonPressed(0) || input.wasPressed('KeyJ');
    if (pressed && hasSword && pl.state !== 'dead' && pl.state !== 'frozen' && pl.state !== 'dodge') {
      if (!this.attacking) { this.combo = 0; this.start(); }
      else if (this.phase > 0.5) this.queued = true;
    }
    if (!this.attacking) { pl.attackT = -1; return; }
    if (pl.state === 'dodge' || !hasSword) { this.t = -1; pl.attackT = -1; return; }
    this.t += dt;
    pl.attackT = this.phase;
    if (this.hitboxActive) {
      const f = _v.set(Math.sin(pl.facing), 0, Math.cos(pl.facing));
      this.hitCenter.copy(pl.position).addScaled(f, 1.15);
      this.hitCenter.y += 0.9;
      this.onHitbox?.(this.hitCenter, this.hitRadius, this.hitThisSwing);
    }
    if (this.t >= SWING_TIME) {
      if (this.queued && this.combo < 2) { this.combo++; this.start(); }
      else { this.t = -1; pl.attackT = -1; this.combo = 0; }
    }
  }
}
const _v = new Vec3();
